import { Tooltip } from '@analog/ui/src';
import allImgPaths from 'assets/allImgPaths';
import Badge from 'components/common/Badge';
import { Skeleton } from 'components/Skeleton';
import TableColumns from 'components/Table/TableColumns';
import { FontColors } from 'consts';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/routesConst';
import { ColumnData } from 'types';
import {
  classes,
  colors,
  colorType,
  DBtoStringValueMapping,
  getIcon,
  getLinkFromNetwork,
  getNetworkName,
  parseNetwork,
  removeZeros,
  timeAgo,
} from 'utils';
import { createDetailNavigation } from 'utils/navigation';

import Chip from '../../../../components/common/Chip';

type dbPhase = 'MESSAGE_RECEIVED' | 'MESSAGE_EXECUTED' | 'COMPLETED';

export const StringToDBValueMapping = {
  received: 'MESSAGE_RECEIVED',
  executed: 'MESSAGE_EXECUTED',
  completed: 'COMPLETED',
  created: 'CREATED',
  processing: 'PROCESSING',
};

export const tooltipOfPhase = {
  MESSAGE_RECEIVED: 'Message has been received by the Timechain for further processing.',
  MESSAGE_EXECUTED: 'Message execution has been tracked on the destination chain.',
  COMPLETED:
    'Message has been fully completed and verified on the destination chain by the Timechain.',
  CREATED: 'Message creation has been recorded on the source chain.',
  PROCESSING: 'Message has been submitted for execution on the destination chain.',
  FAILED: 'Message execution has failed on the destination chain.',
};

export const GMPMessageColumn = () => {
  const navigate = useNavigate();
  const handleMessageClick = createDetailNavigation(navigate, routes.message);
  const columns = [
    {
      title: 'Message ID',
      key: 'messageId',
      render: (data: ColumnData) => (
        <>
          <Tooltip
            id={data[data.columnKey]}
            content={data[data.columnKey]}
            className="!w-full sm:!w-fit !break-words !whitespace-normal z-10 !rounded-lg"
            clickable
          />
          <TableColumns
            withTruncate
            withCopyButton
            dataTooltipId={data[data.columnKey]}
            value={data[data.columnKey] || '-'}
            classNames={{
              content: data[data.columnKey] ? 'cursor-pointer w-fit' : 'w-fit',
              skeletonText: 'h-4 w-24',
              skeletonLabel: 'w-24',
            }}
            textColor={data[data.columnKey] ? FontColors.PURPLE : FontColors.WHITE}
            onClick={() => {
              data[data.columnKey]?.toString() && handleMessageClick(data.messageId.toString());
            }}
          />
        </>
      ),
    },
    {
      title: 'Task IDs',
      key: 'messageReceivedTaskId',
      render: (data: ColumnData) => (
        <>
          <Tooltip
            id={`${data[data.columnKey]?.toString()}_Received`}
            content={data[data.columnKey]?.toString()}
            className="max-w-[240px] !break-words !whitespace-normal z-10 !rounded-lg"
          />

          {data.SubmitGatewayEventTaskId && (
            <Tooltip
              id={`${data.SubmitGatewayEventTaskId.toString().replace(/,/g, '')}_Submitted`}
              content={data.SubmitGatewayEventTaskId.toString().replace(/,/g, '')}
              className="max-w-[240px] !rounded-lg z-10"
            />
          )}
          {data.messageExecutedTaskId && (
            <Tooltip
              id={`${data.messageExecutedTaskId.toString().replace(/,/g, '')}_Executed`}
              content={data.messageExecutedTaskId.toString().replace(/,/g, '')}
              className="max-w-[240px] !rounded-lg z-10"
            />
          )}
          <div className="flex flex-col gap-2 relative">
            <Skeleton.Loader>
              <div className={'flex items-center gap-2 w-full'}>
                <span className="w-[7px] h-[7px] bg-[#ffffffa3] hidden sm:flex rounded-full"></span>
                <TableColumns
                  dataTooltipId={`${data[data.columnKey]?.toString()}_Received`}
                  value={data[data.columnKey] ? 'Message Received' : 'Pending'}
                  classNames={{
                    content: 'cursor-pointer w-fit leading-[22px]',
                    skeletonText: 'h-4 w-10',
                    skeletonLabel: 'w-10',
                  }}
                  textColor={FontColors.PURPLE}
                  onClick={() => {
                    data[data.columnKey]?.toString() &&
                      navigate(`${routes.task}/${data.messageReceivedTaskId.toString()}`);
                  }}
                />
              </div>
              {data.SubmitGatewayEventTaskId && (
                <div className={'flex items-center gap-2 w-full'}>
                  <span className="w-[7px] h-[7px] border border-[#ffffffa3] hidden sm:flex rounded-full before:content before:absolute before:border-r before:border-dashed before:border-[#ffffff3d] before:h-[18px] before:bottom-[17px] before:left-[2.5px] before:border-l before:top-[17px]"></span>

                  <TableColumns
                    dataTooltipId={`${data.SubmitGatewayEventTaskId.toString().replace(
                      /,/g,
                      ''
                    )}_Submitted`}
                    value="Message Submitted"
                    classNames={{
                      content: 'cursor-pointer w-fit leading-[22px]',
                      skeletonText: 'h-4 w-10',
                      skeletonLabel: 'w-10',
                    }}
                    textColor={FontColors.PURPLE}
                    onClick={() => {
                      data.SubmitGatewayEventTaskId?.toString() &&
                        navigate(
                          `${
                            routes.submitGateway
                          }/${data.SubmitGatewayEventTaskId.toString().replace(/,/g, '')}`,
                          { state: { isTaskId: true } }
                        );
                    }}
                  />
                </div>
              )}
              {data.messageExecutedTaskId && (
                <div className={'flex items-center gap-2 w-full'}>
                  <span className="w-[7px] h-[7px] border border-[#ffffffa3] hidden sm:flex rounded-full before:content before:absolute before:border-r before:border-dashed before:border-[#ffffff3d] before:h-[18px] before:bottom-[17px] before:left-[2.5px]"></span>

                  <TableColumns
                    dataTooltipId={`${data.messageExecutedTaskId
                      .toString()
                      .replace(/,/g, '')}_Executed`}
                    value="Message Executed"
                    classNames={{
                      content: 'cursor-pointer w-fit leading-[22px]',
                      skeletonText: 'h-4 w-10',
                      skeletonLabel: 'w-10',
                    }}
                    textColor={FontColors.PURPLE}
                    onClick={() => {
                      data.messageExecutedTaskId?.toString() &&
                        navigate(
                          `${routes.task}/${data.messageExecutedTaskId
                            .toString()
                            .replace(/,/g, '')}`
                        );
                    }}
                  />
                </div>
              )}
            </Skeleton.Loader>
          </div>
        </>
      ),
    },
    {
      title: 'Source',
      key: 'srcAddress',
      render: (data: ColumnData) => {
        return (
          <Skeleton.Loader>
            <div className="flex flex-col gap-2">
              <div className={classes('flex gap-1 flex-wrap')}>
                <TableColumns
                  key={data[data.columnKey]}
                  beforeImagePath={
                    data.srcNetwork?.split('-') && getIcon(data.srcNetwork.split('-')[0])
                  }
                  value={data.srcNetwork || '-'}
                  textColor={FontColors.WHITE}
                  classNames={{
                    container: '!w-fit',
                    base: '!w-fit',
                    content: 'capitalize overflow-visible whitespace-normal word-break-all',
                    skeletonText: 'h-4 w-12',
                  }}
                />
              </div>

              {data[data.columnKey] && (
                <Chip
                  icon={
                    <img
                      src={allImgPaths.destinationAddress}
                      alt="task"
                      className="w-full h-full"
                    />
                  }
                  value={removeZeros(data[data.columnKey])}
                  colIndex={data.messageId}
                  withCopyButton
                  withTruncate
                  copyValue={data[data.columnKey]}
                  dataTooltipId={`${data[data.columnKey]}-salt`}
                  dataTooltipContent={removeZeros(data[data.columnKey])}
                  onClick={() => {
                    const redirectLink = getLinkFromNetwork(
                      `address/${removeZeros(data[data.columnKey])}`,
                      data['srcNetwork'].split('-')[0]
                    );
                    redirectLink && window.open(redirectLink, '_blank');
                  }}
                  classNames={{
                    base: 'md:justify-start',
                    container: 'max-w-fit bg-opacity-[0.16] bg-[#E563F9] pr-0',
                    skeletonText: 'md:w-[100px] w-full',
                    content: 'max-w-auto text-[#E563F9] cursor-pointer overflow-hidden pl-0',
                  }}
                />
              )}
              {data.srcTransactionHash && (
                <Chip
                  icon={
                    <img src={allImgPaths.destinationHash} alt="task" className="w-full h-full" />
                  }
                  value={data.srcTransactionHash || '-'}
                  withCopyButton
                  withTruncate
                  copyValue={data.srcTransactionHash}
                  dataTooltipId={`${data.srcTransactionHash}-srcTxHash`}
                  dataTooltipContent={data.srcTransactionHash}
                  onClick={() => {
                    const redirectLink = getLinkFromNetwork(
                      `tx/${data.srcTransactionHash}`,
                      data['srcNetwork'].split('-')[0]
                    );
                    redirectLink && window.open(redirectLink, '_blank');
                  }}
                  classNames={{
                    base: 'md:justify-start',
                    container: 'max-w-fit pr-0 gap-1',
                    skeletonText: 'md:w-[100px] w-full',
                    content: 'max-w-auto text-[#E563F9] cursor-pointer overflow-hidden pl-0',
                  }}
                />
              )}
            </div>
          </Skeleton.Loader>
        );
      },
    },
    {
      title: 'Destination',
      key: 'destTransactionHash',
      render: (data: ColumnData) => {
        if (!data.destNetwork && !data.destinationAddress && !data.destTransactionHash) {
          return (
            <Skeleton.Loader>
              <span>-</span>
            </Skeleton.Loader>
          );
        }
        const networkName = getNetworkName(data.destNetwork);
        return (
          <Skeleton.Loader>
            <div className="flex flex-col gap-2">
              <div className={classes('flex gap-1 flex-wrap')}>
                <TableColumns
                  key={data[data.columnKey]}
                  beforeImagePath={networkName && getIcon(networkName)}
                  value={parseNetwork(data.destNetwork) || '-'}
                  textColor={FontColors.WHITE}
                  classNames={{
                    container: '!w-fit',
                    base: '!w-fit',
                    content: 'capitalize overflow-visible whitespace-normal word-break-all',
                    skeletonText: 'h-4 w-12',
                  }}
                />
              </div>
              {data.destinationAddress && (
                <Chip
                  icon={
                    <img
                      src={allImgPaths.destinationAddress}
                      alt="task"
                      className="w-full h-full"
                    />
                  }
                  value={data.destinationAddress}
                  colIndex={data.messageId}
                  withCopyButton
                  withTruncate
                  copyValue={data.destinationAddress}
                  dataTooltipId={`${data.destinationAddress}-salt`}
                  dataTooltipContent={data.destinationAddress}
                  onClick={() => {
                    const redirectLink = getLinkFromNetwork(
                      `address/${data.destinationAddress}`,
                      networkName
                    );
                    redirectLink && window.open(redirectLink, '_blank');
                  }}
                  classNames={{
                    base: 'md:justify-start',
                    container: 'max-w-fit bg-opacity-[0.16] bg-[#E563F9] pr-0',
                    skeletonText: 'md:w-[100px] w-full',
                    content: 'max-w-auto text-[#E563F9] cursor-pointer overflow-hidden pl-0',
                  }}
                />
              )}
              {(data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash) && (
                <Chip
                  icon={
                    <img src={allImgPaths.destinationHash} alt="task" className="w-full h-full" />
                  }
                  value={data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash || '-'}
                  withCopyButton
                  withTruncate
                  copyValue={data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash}
                  dataTooltipId={`${
                    data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash
                  }-destTxHash`}
                  dataTooltipContent={
                    data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash
                  }
                  onClick={() => {
                    const redirectLink = getLinkFromNetwork(
                      `tx/${data[data.columnKey] || data?.SubmitGatewayEventTask?.batchTxHash}`,
                      networkName
                    );
                    redirectLink && window.open(redirectLink, '_blank');
                  }}
                  classNames={{
                    base: 'md:justify-start',
                    container: 'max-w-fit pr-0',
                    skeletonText: 'md:w-[100px] w-full',
                    content: 'max-w-auto text-[#E563F9] cursor-pointer overflow-hidden',
                  }}
                />
              )}
            </div>
          </Skeleton.Loader>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (data: ColumnData) => {
        const val: dbPhase = data[data.columnKey]?.toLowerCase();
        return (
          <>
            <Tooltip
              id={tooltipOfPhase[val?.toUpperCase() as keyof typeof tooltipOfPhase]}
              content={tooltipOfPhase[val?.toUpperCase() as keyof typeof tooltipOfPhase]}
              className="max-w-[220px] md:!max-w-fit !rounded-lg z-10"
              clickable
            />
            <Skeleton.Loader className="w-28 h-5">
              <Badge
                label={DBtoStringValueMapping[val as keyof typeof DBtoStringValueMapping]}
                badgeColor={colors[val as keyof typeof colors] as colorType}
                data-tooltip-id={tooltipOfPhase[val?.toUpperCase() as keyof typeof tooltipOfPhase]}
              />
              {data?.SubmitGatewayEventTask?.result?.err && (
                <Badge label="FAILED" badgeColor="red" data-tooltip-id="FAILED" />
              )}
            </Skeleton.Loader>
          </>
        );
      },
    },
    {
      title: 'Created at',
      key: 'created_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]}
          dataTooltipContent={moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')}
          toolTipClass="!w-fit"
        />
      ),
    },
    {
      title: 'Updated at',
      key: 'updated_at',
      render: (data: ColumnData) => (
        <TableColumns
          value={data[data.columnKey] ? timeAgo(new Date(data[data.columnKey])) : '-'}
          dataTooltipId={data[data.columnKey]}
          dataTooltipContent={moment.utc(data[data.columnKey]).format('YYYY-MM-DD hh:mm:ss (UTC)')}
          toolTipClass="!w-fit"
        />
      ),
    },
  ];

  return columns;
};
